import { useEffect, useRef, useState } from 'react'
import { detailFor, dwellMs, iconFor, titleFor, toneFor } from './feedText'
import type { FeedEventView } from '../../adminApi'

/**
 * The feed's toast stack.
 *
 * The app already has a `Toast` — one message, 7-second auto-dismiss — and it is right for
 * *"prediction saved"*. **It is wrong for this feed and is deliberately not stretched to fit:**
 * three goals in a minute is normal, and a feed where the second message erases the first before
 * you read it is worse than no feed.
 *
 * So: a stack, newest at top, capped at four visible with an "+N more" affordance, per-type dwell,
 * and error types that are **sticky until dismissed**.
 *
 * Rendered only inside the backoffice screen. No admin gets goal toasts over the predictions grid
 * — the game screen is the game.
 */

export const MAX_VISIBLE = 4

interface StackEntry {
  event: FeedEventView
  /** Null for a sticky type: it stays until someone dismisses it. */
  expiresAtMs: number | null
}

export default function BackofficeToastStack({
  events,
}: {
  /** Already mute-filtered by the caller — the stack renders what it is given. */
  events: FeedEventView[]
}) {
  const [entries, setEntries] = useState<StackEntry[]>([])
  // Which ids have already been toasted. A toast fires once, on arrival — re-rendering the panel
  // must not replay the afternoon.
  const seen = useRef<Set<number>>(new Set())
  const primed = useRef(false)

  useEffect(() => {
    if (!primed.current) {
      // The first render carries the replay: everything the server already held. Those are
      // history, not news, so they populate the panel and toast nothing.
      for (const event of events) seen.current.add(event.id)
      primed.current = true
      return
    }
    const fresh = events.filter((event) => !seen.current.has(event.id))
    if (fresh.length === 0) return
    for (const event of fresh) seen.current.add(event.id)
    setEntries((previous) => [
      ...fresh
        .slice()
        .sort((a, b) => b.id - a.id)
        .map((event) => {
          const dwell = dwellMs(event.type)
          return { event, expiresAtMs: dwell === null ? null : Date.now() + dwell }
        }),
      ...previous,
    ])
  }, [events])

  // One timer for the whole stack rather than one per toast: a matchday burst would otherwise
  // register a dozen timers a minute, and they all expire against the same clock anyway.
  useEffect(() => {
    if (entries.every((entry) => entry.expiresAtMs === null)) return
    const id = setInterval(() => {
      const now = Date.now()
      setEntries((previous) =>
        previous.filter((entry) => entry.expiresAtMs === null || entry.expiresAtMs > now)
      )
    }, 500)
    return () => clearInterval(id)
  }, [entries])

  if (entries.length === 0) return null

  const visible = entries.slice(0, MAX_VISIBLE)
  const overflow = entries.length - visible.length

  return (
    <div className="bo-toasts" role="status" aria-live="polite">
      {visible.map((entry) => {
        const detail = detailFor(entry.event)
        return (
          <div
            key={entry.event.id}
            className={`bo-toast bo-toast--${toneFor(entry.event.type)}`}
            data-sticky={entry.expiresAtMs === null ? 'true' : 'false'}
          >
            <span aria-hidden="true">{iconFor(entry.event.type)}</span>
            <div className="bo-toast__body">
              <p className="bo-toast__title">{titleFor(entry.event)}</p>
              {detail && <p className="bo-toast__detail">{detail}</p>}
            </div>
            {entry.event.source === 'manual' && <span className="bo-tag bo-tag--manual">manual</span>}
            <button
              type="button"
              className="bo-toast__dismiss"
              aria-label={`Dismiss ${titleFor(entry.event)}`}
              onClick={() =>
                setEntries((previous) => previous.filter((held) => held.event.id !== entry.event.id))
              }
            >
              ×
            </button>
          </div>
        )
      })}
      {overflow > 0 && (
        <button
          type="button"
          className="bo-toasts__overflow"
          onClick={() => setEntries(visible)}
          // Not a count you can ignore: it is the tell that the feed is moving faster than you
          // are reading it, which on a matchday is the normal case.
          title={`${overflow} more event${overflow === 1 ? '' : 's'} arrived`}
        >
          +{overflow} more
        </button>
      )}
    </div>
  )
}
